import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Edit2, Trash2, Check, X, Loader2, ImagePlus, Repeat, Upload, ArrowLeft, Package, Plus } from "lucide-react";
import { apiClient } from "@/lib/api";
import { resolveBackHref } from "@/lib/navTracker";
import { fmt2 } from "@/lib/money";
import CategoryPill from "@/components/CategoryPill";
import { useAuth } from "@/context/AuthContext";
import { itemCost, itemValue, itemCostNZD, itemValueNZD, isValueInferred, itemQty, computeItemValuation } from "@/utils/itemValue";
import InferredValueIndicator from "@/components/InferredValueIndicator";
import { composeItemDescription, composeItemName } from "@/lib/itemDescription";
import RecordSaleModal from "@/components/RecordSaleModal";
import SalesHistoryTable from "@/components/SalesHistoryTable";

/**
 * ItemDetail — read-only view of an item with its dynamic fields.
 * Legacy hardcoded fields (brand/model/year/condition/cost_price_ex_gst/etc.)
 * are intentionally NOT shown. They remain in the DB.
 */
export default function ItemDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const [category, setCategory] = useState(null);
  const [fields, setFields] = useState([]);
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sales, setSales] = useState([]);
  const [saleModalOpen, setSaleModalOpen] = useState(false);
  // Monotonic counter bumped whenever the Item Detail view needs a full
  // reload (e.g. after a sale is recorded). Tying this into the main
  // loader's dep array guarantees item + sales + category + fields +
  // collection all refresh together so stale state can never persist.
  const [refreshTick, setRefreshTick] = useState(0);

  // Back — explicit parent route: always /collections/{collection_id}.
  // No history/tracker consulted. Falls back to /items only while the
  // item is still loading (collection_id not yet known).
  const backHref = useMemo(() => {
    return item?.collection_id ? `/collections/${item.collection_id}` : "/items";
  }, [item?.collection_id]);

  // Industry-standard valuation (2026-02-25 hierarchy update).
  // `computeItemValuation` now auto-selects Manual → Market → Most
  // Recent Sale → Buy Price purely from data presence, so the legacy
  // "no sales → estimated_value_ex_gst" override is redundant. Reuses
  // the same `sales` state already fetched for the Sales History table
  // below to avoid a duplicate HTTP call.
  const valuation = useMemo(
    () => computeItemValuation(item || {}, sales || []),
    [item, sales],
  );

  useEffect(() => {
    (async () => {
      try {
        const { data: it } = await apiClient.get(`/items/${id}`);
        setItem(it);
        // Sales history for this specific item (kept separate from item
        // load so a stale sales table never blocks the main content).
        try {
          const { data: s } = await apiClient.get(`/sales?item_id=${id}`);
          setSales(s || []);
        } catch { /* tolerate — table just shows the empty state */ }
        if (it.category_id) {
          const [cats, fs] = await Promise.all([
            apiClient.get("/categories"),
            apiClient.get(`/categories/${it.category_id}/fields`),
          ]);
          setCategory((cats.data || []).find((c) => c.id === it.category_id) || null);
          setFields(fs.data || []);
        }
        if (it.collection_id) {
          try {
            const { data } = await apiClient.get(`/collections/${it.collection_id}`);
            setCollection(data);
          } catch { /* */ }
        }
        // Persist last-visited context so the Home "Continue" pill can offer
        // a one-tap return to this exact item. Both keys are written so the
        // pill can render the full "Collection → Item" trail. Silently
        // tolerated if storage is unavailable (private mode).
        try {
          if (it.collection_id) localStorage.setItem("cdi.lastCollectionId", it.collection_id);
          localStorage.setItem("cdi.lastItemId", it.id);
        } catch (_e) { /* noop */ }
      } catch {
        toast.error("Failed to load item.");
      } finally { setLoading(false); }
    })();
  }, [id, refreshTick]);

  const remove = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/items/${id}`);
      toast.success("Deleted.");
      navigate(item?.collection_id ? `/collections/${item.collection_id}` : "/items");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
      setDeleting(false);
    }
  };

  // === Image gallery: single unified collection that combines the primary
  //     image (`item.image`) and the additional photos (`item.images[]`).
  //     ItemEntry.jsx writes the first uploaded photo to `item.image` and the
  //     rest to `item.images`, so reading only `item.images` here would miss
  //     the primary photo (the bug). We always read the combined list and,
  //     on every write, redistribute back so index 0 → `item.image` and the
  //     rest → `item.images[]`. The Items list reads `item.image` for its
  //     thumbnail, so this binding stays consistent across screens. ===
  const galleryImages = [item?.image, ...((item?.images) || [])].filter(Boolean);

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  // Persist a redistributed list: first slot → primary image (`image`),
  // remainder → additional photos (`images[]`). Sending both fields in one
  // PATCH ensures the screen state matches the database after every edit.
  const persistImages = async (combined) => {
    const cleaned = (combined || []).filter(Boolean);
    const body = {
      image: cleaned[0] || null,   // null clears Optional[str] on the backend
      images: cleaned.slice(1),
    };
    const { data } = await apiClient.patch(`/items/${id}`, body);
    setItem(data);
  };

  const addImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset input
    if (!file) return;
    // Fix #12 — Photo Limits. Basic caps at 3, Premium at 50.
    // ``isPremium`` is hardcoded false until auth lands (mirrors
    // Dashboard convention). ``galleryImages`` already includes the
    // primary photo at index 0, so it IS the full item photo count.
    const isPremium = !!user?.premium_plan;
    const MAX_IMAGES = isPremium ? 50 : 3;
    if (galleryImages.length >= MAX_IMAGES) {
      toast.error(isPremium
        ? "Premium plan allows up to 50 photos."
        : "Basic plan allows up to 3 photos.");
      return;
    }
    try {
      const url = await fileToDataUrl(file);
      await persistImages([...galleryImages, url]);
      toast.success("Image added.");
    } catch { toast.error("Failed to upload image."); }
  };

  const replaceImage = async (idx, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const url = await fileToDataUrl(file);
      const next = galleryImages.slice();
      next[idx] = url;
      await persistImages(next);
      toast.success("Image replaced.");
    } catch { toast.error("Failed to replace image."); }
  };

  const removeImage = async (idx) => {
    try {
      const next = galleryImages.filter((_, i) => i !== idx);
      await persistImages(next);
      toast.success("Image removed.");
    } catch { toast.error("Failed to remove image."); }
  };

  // === Primary image (item.image) — single image at the top of the page ===
  const primaryImageInputRef = useRef(null);

  const persistPrimaryImage = async (value) => {
    // value: data URL to set, or null to clear (backend honours explicit null
    // on Item.image since it's typed Optional[str] = None in ItemUpdate).
    const { data } = await apiClient.patch(`/items/${id}`, { image: value });
    setItem(data);
  };

  const onPrimaryImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image must be smaller than 4 MB.");
      return;
    }
    try {
      const url = await fileToDataUrl(file);
      await persistPrimaryImage(url);
      toast.success(item.image ? "Image replaced." : "Image added.");
    } catch { toast.error("Failed to upload image."); }
  };

  const removePrimaryImage = async () => {
    try {
      await persistPrimaryImage(null);
      toast.success("Image removed.");
    } catch { toast.error("Failed to remove image."); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10"><div className="vp-card p-8 vp-skeleton h-64" /></div>;
  if (!item) return null;

  const data = item.item_data || {};
  // Title — user-typed Item Name is authoritative and NEVER overridden
  // by the composed AUTO-NAME (2026-02 persistence rule). The composer
  // is only consulted when `item_data["Item Name"]` is genuinely empty
  // (blank string / undefined / whitespace), which mirrors the
  // save-path invariant: whatever the user typed round-trips verbatim.
  // Legacy top-level name fields (`item.name` / `item.item_name`) are
  // last-resort fallbacks for orphan records that pre-date the
  // item_data schema.
  const rawItemName = String(data["Item Name"] ?? "").trim();
  const composedName = composeItemName(item, collection);
  const displayTitle = rawItemName
    || composedName
    || item.name
    || item.item_name
    || "Untitled";
  // Item Description — sourced from the shared helper so ItemDetail's
  // subtitle and Collection table rows stay in lockstep. Per-type
  // formats live inside `composeItemDescription`.
  const itemDescription = composeItemDescription(item, collection);
  // Migrate legacy values for display (read-only, will get persisted on next edit)
  const legacyFallback = {};
  if (Object.keys(data).length === 0) {
    if (item.brand) legacyFallback["Brand"] = item.brand;
    if (item.model) legacyFallback["Model"] = item.model;
    if (item.year)  legacyFallback["Year"] = item.year;
    if (item.condition) legacyFallback["Condition"] = item.condition;
  }
  const merged = { ...legacyFallback, ...data };

  // Unified Case Math renderer (Fix #8 Part 2).
  //
  // Wine, Whisky, and Spirits all show the same case-quantity block
  // in the same order with the same derived math. Non-bottle
  // collections (coins, comics, etc.) are untouched.
  //
  // Display order (identical for all three collections):
  //   1. Case Mode
  //   2. Case Size (or legacy Units in Case, only when set)
  //   3. Total Bottles
  //   4. Bottle Count           (= Total Bottles, per spec)
  //   5. Number of Cases        (only when Case Size > 1)
  //   6. Loose Bottles          (only when Case Size > 1)
  //
  // Derivation:
  //   • Number of Cases = floor(Total Bottles / Case Size)
  //   • Loose Bottles   = Total Bottles mod Case Size
  //
  // When Case Size is empty / null / 0 / 1, rows 5 & 6 are hidden and
  // Case Mode is forced to "Single Bottle". Legacy items with only a
  // `Bottle Count` (no Total Bottles) round-trip: Total Bottles is
  // treated as equal to Bottle Count for display purposes.
  //
  // IMPORTANT: This block is DISPLAY ONLY. It never mutates the saved
  // item and never affects analytics — Dashboard totals, collection
  // totals, and profit/loss all read the persisted `Total Bottles`
  // (or use `items.length`) and are unaffected by the derivation
  // performed here.
  const isBottleCollection = (() => {
    if (!collection) return false;
    if (collection.type_id === "wine") return true;
    if (collection.type_id === "whisky") return true;
    if (collection.type_id === "spirits") return true;
    const n = (collection.name || "").toLowerCase();
    if (/wine|whisk(e)?y/i.test(n)) return true;
    return /(rum|tequila|cognac|gin|vodka|mezcal|armagnac|brandy|liqueur|spirits?)/i.test(n);
  })();
  if (isBottleCollection) {
    // Quantity display resolver (2026-02 collection-totals alignment).
    //
    // Reads the SAME priority list as itemQty() /
    // _read_qty_from_item() so the ItemDetail Quantity block never
    // diverges from the Dashboard / CollectionDetail rollups:
    //
    //   Bottle Count → Total Bottles → Quantity Owned
    //
    // Stored 0 in one key is skipped so it can't mask a truthy value
    // in the next. Case Size / Number of Cases / Loose Bottles are
    // read directly from item_data (they are now persisted by the
    // save path). When Number of Cases / Loose Bottles are missing
    // — e.g. legacy items that pre-date the save-path fix — we
    // derive them from Total Bottles + Case Size for display only.
    // Legacy field aliases (`Quantity`, `Units`, `BottleQuantity`)
    // are NOT consulted.
    // 2026-02-26 — Quantity Migration Step 2. Prefer the top-level
    // structured columns (`item.total_units` / `item.case_size` /
    // `item.cases` / `item.loose_units`) as the source of truth, and
    // fall back to the legacy `item_data` mirror only when the
    // structured column is missing (pre-migration items). Every
    // derivation retains the existing `caseSize > 1` guard so Single
    // Bottle mode (null / 0 / 1 Case Size) continues to skip the
    // divide/modulo entirely.
    const qty = itemQty({ total_units: item.total_units, item_data: merged });
    const hasQtyKey =
      (item.total_units !== undefined && item.total_units !== null)
      || (merged["Bottle Count"]  !== undefined && merged["Bottle Count"]  !== null && merged["Bottle Count"]  !== "")
      || (merged["Total Bottles"] !== undefined && merged["Total Bottles"] !== null && merged["Total Bottles"] !== "");
    const totalBottles = qty > 0 || hasQtyKey ? qty : null;

    const rawCase = (item.case_size !== undefined && item.case_size !== null)
      ? item.case_size
      : (merged["Case Size"] ?? merged["Units in Case"]);
    const caseSize = Number.isFinite(Number(rawCase)) && Number(rawCase) > 1
      ? Number(rawCase)
      : 0;

    // Stored values win — they are the exact numbers the save path
    // wrote. Derived fallbacks are only used when the stored key is
    // missing (legacy items).
    const storedNoC = (item.cases !== undefined && item.cases !== null)
      ? item.cases
      : merged["Number of Cases"];
    const numberOfCases = (storedNoC !== undefined && storedNoC !== null && storedNoC !== "")
      ? Number(storedNoC)
      : (caseSize > 1 && totalBottles !== null ? Math.floor(totalBottles / caseSize) : null);
    const storedLoose = (item.loose_units !== undefined && item.loose_units !== null)
      ? item.loose_units
      : merged["Loose Bottles"];
    const looseBottles = (storedLoose !== undefined && storedLoose !== null && storedLoose !== "")
      ? Number(storedLoose)
      : (caseSize > 1 && totalBottles !== null ? totalBottles % caseSize : null);

    // Case Mode derivation (2026-10 unified rule). Case Mode is
    // determined strictly from `case_size`: when case_size > 1 the
    // item is in Case mode; otherwise it falls back to Single Bottle.
    // Number of Cases / Total Bottles / Loose Bottles no longer gate
    // the mode — they are display values derived from this choice.
    const caseMode = caseSize > 1 ? "Case" : "Single Bottle";

    // Strip every quantity-related key from merged, then reinsert in
    // the mandated display order. Any stale legacy quantity keys
    // (Case Type, etc.) are left untouched.
    const {
      "Case Mode":       _cm,
      "Case Size":       _cs,
      "Units in Case":   _uic,
      "Total Bottles":   _tb,
      "Bottle Count":    _bc,
      "Number of Cases": _noc,
      "Loose Bottles":   _lb,
      ...rest
    } = merged;
    void _cm; void _cs; void _uic; void _tb; void _bc; void _noc; void _lb;
    Object.keys(merged).forEach((k) => { delete merged[k]; });
    Object.assign(merged, rest);

    // Unconditional reinsertion (2026-02): re-add every quantity/case
    // key back onto `merged` in the fixed CDI order so the read-only
    // Quantity grid always renders all 7 rows. Values come from the
    // resolver (Total Bottles / Bottle Count) or the stored keys
    // (Case Size / Number of Cases / Loose Bottles) — see the top
    // of this block for the priority rules. Empty values render as
    // FieldDisplay's em-dash placeholder so the layout stays stable
    // across items.
    merged["Total Bottles"]   = totalBottles !== null ? totalBottles : "";
    merged["Case Mode"]       = caseMode;
    merged["Case Size"]       = caseSize > 1
      ? caseSize
      : (Number(_uic) > 1 ? Number(_uic) : "");
    merged["Number of Cases"] = numberOfCases !== null ? numberOfCases : "";
    merged["Loose Bottles"]   = looseBottles !== null ? looseBottles : "";
    merged["Bottle Count"]    = totalBottles !== null ? totalBottles : "";
  }

  // Order fields by schema; show ad-hoc keys at the end. Legacy single-
  // value pricing fields are filtered out — they're now displayed by the
  // unified Buy/Sell × Ex/Inc GST card in the sidebar so showing them again
  // in Item Details would duplicate the information (and on mobile causes
  // the "legacy pricing rows" the user reported).
  const LEGACY_PRICING = new Set([
    "Purchase Price", "Purchase price", "purchase_price",
    "Estimated Value", "Estimated value", "estimated_value",
    "Buy Price", "Sell Price",
    "_pricing_mode",   // internal flag — read by the "Entered as:" footer, not displayed as a field
  ]);
  // Spirits detection mirrors ItemEntry / itemDescription helpers — matches
  // rum / tequila / cognac / gin / vodka / mezcal / armagnac / brandy /
  // liqueur / generic "spirits" collections. Whisky is explicitly
  // excluded — it has its own separate identity track.
  const isSpiritsCollection = (() => {
    if (!collection) return false;
    if (collection.type_id === "spirits") return true;
    if (collection.type_id === "whisky") return false;
    const n = collection.name || "";
    if (/whisk(e)?y/i.test(n)) return false;
    return /(rum|tequila|cognac|gin|vodka|mezcal|armagnac|brandy|liqueur|spirits?)/i.test(n);
  })();
  // Collection-type helpers used by the render filters below.
  const isWhiskyCollection = collection?.type_id === "whisky"
    || (!collection?.type_id && /whisk(e)?y/i.test(collection?.name || ""));
  const isWineCollection = collection?.type_id === "wine"
    || (!collection?.type_id && /wine/i.test(collection?.name || ""));
  // Spirits identity whitelist for the Item Details key/value dump —
  // now disabled (2026-02) so Spirits renders every field just like
  // Wine does, showing empty ones as an em-dash. `passesSpiritsFilter`
  // falls through when the whitelist is null.
  const SPIRITS_ALLOWED_KEYS = null;
  const passesSpiritsFilter = (k) => !SPIRITS_ALLOWED_KEYS || SPIRITS_ALLOWED_KEYS.has(k);
  // Whisky Item Details hide-list — now disabled (2026-02) so ABV and
  // ABV % render alongside every other field like Wine and Spirits.
  const WHISKY_HIDE_KEYS = null;
  const passesWhiskyFilter = (k) => !WHISKY_HIDE_KEYS || !WHISKY_HIDE_KEYS.has(k);
  const orderedKeys = [
    ...fields.map((f) => f.field_name).filter((k) => !LEGACY_PRICING.has(k) && passesSpiritsFilter(k) && passesWhiskyFilter(k)),
    ...Object.keys(merged).filter((k) => !fields.find((f) => f.field_name === k) && !LEGACY_PRICING.has(k) && passesSpiritsFilter(k) && passesWhiskyFilter(k)),
  ];
  const symbol = collection?.currency_symbol || "$";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="item-detail">
      {/* Mobile-only Back affordance — context-aware: returns to the exact
          screen the user came from (Items list, Collection detail, Category-
          filtered list, etc.). The origin URL is passed via `<Link state={{
          from }}>` by the calling pages and persisted in sessionStorage so
          a reload doesn't break the return path. Falls back to `/items`
          (global list) only when no origin is known (deep link / external
          referrer). Desktop is untouched. */}
      <button
        type="button"
        onClick={() => navigate(backHref, { replace: true })}
        data-testid="id-back"
        aria-label="Back to previous screen"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-3 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>

      {/* Breadcrumb: [Collection] → Items / [Item] */}
      <nav
        aria-label="Breadcrumb"
        data-testid="id-breadcrumb"
        className="flex items-center gap-1.5 flex-wrap text-xs mb-4"
      >
        <Link
          to="/"
          data-testid="id-breadcrumb-home"
          className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
        >
          Home
        </Link>
        {collection && <span className="vp-muted">→</span>}
        {collection ? (
          <Link
            to={`/collections/${collection.id}`}
            data-testid="id-breadcrumb-collection"
            title="Open collection"
            className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
          >
            {collection.name}
          </Link>
        ) : null}
        <span className="vp-muted">→</span>
        <Link
          to="/items"
          data-testid="id-breadcrumb-items"
          className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
        >
          Items
        </Link>
        <span className="vp-muted">/</span>
        <span
          data-testid="id-breadcrumb-current"
          className="text-xs font-medium truncate max-w-[40ch]"
          title={displayTitle}
        >
          {displayTitle}
        </span>
      </nav>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6 landscape:mb-4">
        <div className="min-w-0 flex-1 basis-full sm:basis-auto max-w-full sm:max-w-[70ch]">
          <div className="flex items-center gap-2 flex-wrap mb-2" data-testid="id-meta">
            {/* Category pill removed from ItemDetail meta row — Category
                is no longer part of the header metadata (see "identity
                refactor — global metadata wiring" ticket). Collection
                link + Case Lot pill + remaining meta stay unchanged. */}
            {collection && (
              <Link
                to={`/collections/${collection.id}`}
                data-testid="id-collection-link"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] hover:opacity-90"
              >
                {collection.name}
              </Link>
            )}
            {/* Case Lot pill — shown only when the item carries case
                metadata in item_data. Mirrors the existing pill styling
                (no layout redesign) so it slots into the metadata row
                next to the Category / Collection pills. */}
            {(() => {
              const ct = item?.item_data?.["Case Type"];
              if (!ct || ct === "Single Bottles") {
                return null;
              }
              const units = Number(item?.item_data?.["Units in Case"]);
              const qty   = Number(item?.item_data?.["Quantity Owned"]);
              let label;
              if (ct === "Full Case") {
                label = Number.isFinite(units) ? `Full Case (${units} bottles)` : "Full Case";
              } else if (ct === "Partial Case") {
                if (Number.isFinite(units) && Number.isFinite(qty)) {
                  label = `Partial Case (${qty} of ${units})`;
                } else if (Number.isFinite(units)) {
                  label = `Partial Case (of ${units})`;
                } else {
                  label = "Partial Case";
                }
              } else {
                label = ct;
              }
              return (
                <span
                  data-testid="id-case-pill"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
                  title={`Case: ${label}`}
                >
                  <Package size={11} />
                  Case: {label}
                </span>
              );
            })()}
          </div>
          {(() => {
            // Title — single source of truth. `displayTitle` is already
            // recomposed by composeItemName() on hydration for Wine /
            // Whisky / Spirits collections when Name Override is not set,
            // so a plain <h1> is all we need. Legacy structured blocks
            // (which read old keys like Brand / Product / Region /
            // Cuvée / Classification and bypassed the composer) removed
            // in Phase C — they were the source of stale identity
            // strings surviving on load.
            return (
              <h1
                className="text-base font-bold tracking-tight break-words [overflow-wrap:anywhere] max-w-full [hyphens:auto] line-clamp-3 sm:line-clamp-none landscape:max-lg:line-clamp-2 pr-2"
                data-testid="id-title"
              >
                {displayTitle}
              </h1>
            );
          })()}
          <p className="text-sm vp-muted mt-2 landscape:max-lg:mt-1 break-words [overflow-wrap:anywhere] max-w-full line-clamp-2 sm:line-clamp-3 landscape:max-lg:line-clamp-2 pr-2" data-testid="id-subtitle">{itemDescription}</p>
          {item.invoice_number && <p className="text-sm vp-muted mt-2">Invoice {item.invoice_number}</p>}
        </div>
      </div>

      {/* Action button group — moved out of the flex header so the
          buttons sit on their own line directly below the item title
          and identity summary. Also introduces a "Create Item" shortcut
          scoped to the current collection so users can add a sibling
          bottle without bouncing back to the collection page. */}
      <div className="flex flex-wrap gap-2 mt-4">
        <Link
          to={`/items/${id}/edit`}
          state={{ from: location.state?.from }}
          data-testid="id-edit"
          className="vp-btn vp-btn-secondary vp-btn-sm"
        >
          <Edit2 size={14} /> Edit
        </Link>
        {confirmDelete ? (
          <div
            className="flex items-center gap-1 bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-md p-1 shadow-sm"
            data-testid="id-delete-confirm"
          >
            <button
              type="button"
              onClick={remove}
              disabled={deleting}
              data-testid="id-delete-yes"
              aria-label="Confirm delete item"
              className="vp-btn vp-btn-sm !h-8 !px-2 bg-[hsl(var(--loss))] text-white hover:opacity-90 disabled:opacity-50"
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              <span className="text-xs font-medium">Delete</span>
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
              data-testid="id-delete-cancel"
              aria-label="Cancel delete"
              className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0 border border-[hsl(var(--border))]"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button onClick={() => setConfirmDelete(true)} data-testid="id-delete" className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--loss))] text-[hsl(var(--loss))]">
            <Trash2 size={14} /> Delete
          </button>
        )}
        {itemQty(item) > 0 && (
          <button
            onClick={() => setSaleModalOpen(true)}
            data-testid="id-record-sale"
            className="vp-btn vp-btn-primary vp-btn-sm"
          >
            <Package size={14} /> Record Sale
          </button>
        )}
        <button
          onClick={() => navigate(`/items/new?collection_id=${item.collection_id}`)}
          data-testid="id-create-item"
          className="vp-btn vp-btn-secondary vp-btn-sm"
        >
          <Plus size={14} /> Create Item
        </button>
      </div>

      {/* Photos gallery — read-only display of the item's photo collection.
          All upload / replace / remove interactions live in Edit mode
          (ItemEntry.jsx); this surface is for viewing only. */}
      <ItemGallery images={galleryImages} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <aside className="lg:col-span-1 vp-fade-in">
          <div className="vp-card p-5 sm:p-6 space-y-3.5 text-sm">
            {/* Unified pricing display — Buy / Sell × Ex / Inc GST. Reads
                the canonical fields directly; itemCost / itemValue (used by
                P/L below + Dashboard) prefer the same canonical fields too. */}
            {(() => {
              const sym = symbol;
              const cur = item?.currency_code;
              const isForeign = String(cur || "NZD").toUpperCase() !== "NZD";
              // Tax label — read verbatim from item.tax_name (resolved at
              // save time in ItemEntry). Simple inline fallback to "Tax"
              // when the item pre-dates the tax_name save-path.
              const taxLabel = item?.tax_name || "Tax";
              const fxP = Number(item.fx_rate_purchase) > 0 ? Number(item.fx_rate_purchase) : 0;
              const fxS = Number(item.fx_rate_sale) > 0 ? Number(item.fx_rate_sale) : 0;
              const fmt = (v) => `${sym}${fmt2(v)}`;
              // Per-row NZD conversion. Cost-side rows use fx_rate_purchase;
              // value-side rows use fx_rate_sale with a fallback to
              // fx_rate_purchase — matching itemCostNZD/itemValueNZD.
              // Tax order of operations: native Ex → fx → apply dynamic tax.
              // Tax rate: prefer the item's stored `tax_percent` (historical
              // snapshot); fall back to the global `cdi.settings.taxPercent`
              // for items saved before per-item tax was stored.
              // Quantity: bottle count multiplies VALUE only (never cost),
              // matching itemValueNZD. Buy rows use qtyMult=1; Sell rows use
              // qtyMult=Total Bottles (fallback 1) so the Sell "≈ NZD" line
              // equals this item's contribution to Collection Totals exactly.
              const _globalTaxPct = (() => {
                try {
                  const raw = typeof window !== "undefined"
                    ? window.localStorage.getItem("cdi.settings.taxPercent")
                    : null;
                  const n = Number(raw);
                  return Number.isFinite(n) && n >= 0 ? n : 0;
                } catch { return 0; }
              })();
              const _itemTaxPct = (() => {
                const stored = item.tax_percent;
                const hasStored = stored !== null && stored !== undefined && stored !== "";
                const n = hasStored ? Number(stored) : NaN;
                return Number.isFinite(n) && n >= 0 ? n : _globalTaxPct;
              })();
              const taxMultiplier = 1 + (_itemTaxPct / 100);
              const exFromInc = (inc) => (Number(inc) || 0) / taxMultiplier;
              const buyExNet  = Number(item.cost_price_ex_gst)      || exFromInc(item.cost_price_inc_gst || item.purchase_price);
              const sellExNet = Number(item.estimated_value_ex_gst) || exFromInc(item.estimated_value_inc_gst || item.estimated_value);
              // Bug fix (2026-09): this used to read Total Bottles / Bottle
              // Count / Quantity Owned via a `??` chain, which does not
              // skip a stored empty string — an item with Total Bottles=""
              // and a real Bottle Count silently fell back to sellQty=1
              // (wrong quantity multiplier on the Market Value row below).
              // Reuses the shared, already-corrected \`itemQty\` helper so
              // there's one source of truth instead of a third inline copy.
              const sellQtyRaw = itemQty(item);
              const sellQty = sellQtyRaw > 0 ? sellQtyRaw : 1;
              const rows = [
                { test: "id-pricing-buy-ex",  label: "Buy Price",  gst: `Ex ${taxLabel} — ${cur}`,  v: item.cost_price_ex_gst,                              fx: fxP,       exNet: buyExNet,  incMult: 1,             qtyMult: 1       },
                { test: "id-pricing-buy-inc", label: "Buy Price",  gst: `Inc ${taxLabel} — ${cur}`, v: item.cost_price_inc_gst || item.purchase_price,     fx: fxP,       exNet: buyExNet,  incMult: taxMultiplier, qtyMult: 1       },
                { test: "id-pricing-sell-ex", label: "Market Value", gst: `Ex ${taxLabel} — ${cur}`,  v: item.estimated_value_ex_gst || item.estimated_value, fx: fxS || fxP, exNet: sellExNet, incMult: 1,             qtyMult: sellQty },
                { test: "id-pricing-sell-inc",label: "Market Value", gst: `Inc ${taxLabel} — ${cur}`, v: item.estimated_value_inc_gst,                        fx: fxS || fxP, exNet: sellExNet, incMult: taxMultiplier, qtyMult: sellQty },
              ];
              return rows.map((r) => (
                <div key={r.test} className="flex flex-col gap-0.5" data-testid={r.test}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="vp-label inline-flex items-baseline gap-1 whitespace-nowrap min-w-0">
                      {r.label}
                      <span
                        className="text-[9px] uppercase tracking-wider vp-muted"
                        data-testid={`${r.test}-gst`}
                      >
                        ({r.gst})
                      </span>
                    </span>
                    <span className="font-mono text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] font-semibold inline-flex items-baseline gap-2 whitespace-nowrap shrink-0 tabular-nums">
                      {fmt(r.v)}
                      <span className="text-[9px] font-sans font-medium tracking-wider uppercase px-1.5 py-0.5 rounded border border-[hsl(var(--border))] vp-muted leading-none">
                        {cur}
                      </span>
                    </span>
                  </div>
                </div>
              ));
            })()}
            {(() => {
              // Profit / Loss row — rendered in the item's native currency
              // for readability. When the item is foreign-currency we
              // ALSO render an "≈ NZD" line so the user sees the number
              // that will feed collection/portfolio rollups.
              // Quantity-aware (2026-09 fix): previously compared PER-UNIT
              // cost vs value with no quantity multiplier, so an item with
              // remaining_qty=0 (fully sold/removed) still showed a nonzero
              // P/L here while the valuation block below (quantity-aware via
              // valuation.total_cost/current_value) correctly showed $0.
              // Reusing those same server-computed figures keeps both tiles
              // consistent.
              const cost = Number(valuation.total_cost) || 0;
              const val = Number(valuation.current_value) || 0;
              if (cost === 0 && val === 0) return null;
              // 2026-02-26 — when the item has no valuation set the P/L
              // row switches to a neutral "Awaiting Valuation" placeholder
              // with the standard tooltip. Total Cost stays visible in the
              // block below; only value / P/L rows are gated.
              if (!valuation.includeInValuationTotals) {
                return (
                  <div className="flex flex-col gap-0.5" data-testid="id-pricing-pl">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <span className="vp-label inline-flex items-baseline gap-1 whitespace-nowrap min-w-0">
                        Profit / Loss
                      </span>
                      <span
                        className="font-mono font-semibold whitespace-nowrap shrink-0 tabular-nums vp-muted italic"
                        title="No items have a valuation set. Profit/Loss will appear once at least one item has a market value."
                      >
                        Awaiting Valuation
                      </span>
                    </div>
                  </div>
                );
              }
              const pl = val - cost;
              const sign = pl > 0 ? "+" : pl < 0 ? "−" : "";
              const tone = pl > 0 ? "text-[hsl(var(--gain))]" : pl < 0 ? "text-[hsl(var(--loss))]" : "vp-muted";
              const cur = item.currency_code || "NZD";
              const isForeign = String(cur).toUpperCase() !== "NZD";
              const plNZD = itemValueNZD(item) - itemCostNZD(item);
              const signNZD = plNZD > 0 ? "+" : plNZD < 0 ? "−" : "";
              return (
                <div className="flex flex-col gap-0.5" data-testid="id-pricing-pl">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="vp-label inline-flex items-baseline gap-1 whitespace-nowrap min-w-0">
                      Profit / Loss
                      <span className="text-[9px] uppercase tracking-wider vp-muted" data-testid="id-gst-label-pl">
                        (Ex {(item.tax_name === "GST" || item.tax_name === "VAT" || item.tax_name === "Sales Tax") ? item.tax_name : "Tax"} basis)
                      </span>
                    </span>
                    <span className={`font-mono font-semibold whitespace-nowrap shrink-0 tabular-nums ${tone}`}>
                      {sign}{symbol}{fmt2(Math.abs(pl))}
                    </span>
                  </div>
                </div>
              );
            })()}
            {/* GST mode footer for the price section. Derived from the item's
                existing GST-prefixed fields: if a non-zero `gst` amount or a
                populated `cost_price_inc_gst` exists, prices were entered Inc
                GST; otherwise the item is Ex GST. Read-only indicator. Mode
                value is rendered as a small pill so it visually matches the
                NZD currency chips on the pricing rows above. */}
            <div
              className="pt-3 border-t border-[hsl(var(--border))] flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1"
              data-testid="id-gst-mode"
            >
              <span className="text-[10px] uppercase tracking-wider vp-muted whitespace-nowrap">
                Entered as
              </span>
              <span className="text-[10px] font-sans font-medium tracking-wider uppercase px-1.5 py-0.5 rounded border border-[hsl(var(--border))] text-[hsl(var(--fg))] whitespace-nowrap shrink-0 leading-none">
                {(() => {
                  const explicit = (item.item_data || {})._pricing_mode;
                  const mode = explicit === "inc" || explicit === "ex"
                    ? explicit
                    : ((Number(item.gst) > 0 || Number(item.cost_price_inc_gst) > 0) ? "inc" : "ex");
                  const _tn = item.tax_name;
                  const label = (_tn === "GST" || _tn === "VAT" || _tn === "Sales Tax") ? _tn : "Tax";
                  return mode === "inc" ? `Inc ${label}` : `Ex ${label}`;
                })()}
              </span>
            </div>
            {/* Industry-standard valuation tiles (2026-02).
                Wire-up per spec — 8 rows read from `valuation` which is
                the client-side computeItemValuation output. Uses the
                same sidebar row styling as the pricing rows above so no
                new CSS / layout is introduced. */}
            {(() => {
              const money = (n) => `${symbol}${fmt2(Number(n) || 0)}`;
              const pct = (n) => `${(Number(n) || 0) >= 0 ? "" : "−"}${fmt2(Math.abs(Number(n) || 0))}%`;
              const qty = Number(valuation.remaining_qty) || 0;
              // 2026-02-26 — gate value / P/L rows on the canonical
              // includeInValuationTotals flag. When false, Unrealised /
              // Total P/L / P/L % render neutral "Awaiting Valuation"
              // placeholders while
              // Total Cost and Current Value stay visible ($0.00 for
              // unvalued items is correct — money(0) already renders
              // that). Realised Gains keeps its number so historical
              // sales still surface even without a current valuation.
              const valued = valuation.includeInValuationTotals;
              const plPlaceholder = { value: "Awaiting Valuation", tone: "vp-muted italic" };
              const plTitle = "No items have a valuation set. Profit/Loss will appear once at least one item has a market value.";
              const rows = [
                { test: "id-val-current-value",   label: "Current Value",        value: money(valuation.current_value) },
                { test: "id-val-total-cost",      label: "Total Cost",           value: money(valuation.total_cost) },
                { test: "id-val-realised",        label: "Realised Gains",       value: money(valuation.realised_PL),
                  tone: (Number(valuation.realised_PL) || 0) > 0 ? "text-[hsl(var(--gain))]" :
                        (Number(valuation.realised_PL) || 0) < 0 ? "text-[hsl(var(--loss))]" : "" },
                { test: "id-val-unrealised",      label: "Unrealised Gains/Loss",
                  ...(valued
                    ? { value: money(valuation.unrealised_PL),
                        tone: (Number(valuation.unrealised_PL) || 0) > 0 ? "text-[hsl(var(--gain))]" :
                              (Number(valuation.unrealised_PL) || 0) < 0 ? "text-[hsl(var(--loss))]" : "" }
                    : { ...plPlaceholder, title: plTitle }) },
                { test: "id-val-total-pl",        label: "Total P/L",
                  ...(valued
                    ? { value: money(valuation.total_PL),
                        tone: (Number(valuation.total_PL) || 0) > 0 ? "text-[hsl(var(--gain))]" :
                              (Number(valuation.total_PL) || 0) < 0 ? "text-[hsl(var(--loss))]" : "" }
                    : { ...plPlaceholder, title: plTitle }) },
                { test: "id-val-pl-percent",      label: "P/L %",
                  ...(valued
                    ? { value: pct(valuation.PL_percent),
                        tone: (Number(valuation.PL_percent) || 0) > 0 ? "text-[hsl(var(--gain))]" :
                              (Number(valuation.PL_percent) || 0) < 0 ? "text-[hsl(var(--loss))]" : "" }
                    : { ...plPlaceholder, title: plTitle }) },
                { test: "id-val-remaining-qty",   label: "Remaining Qty",        value: String(qty) },
                { test: "id-val-source",          label: "Valuation Source",
                  value: (String(valuation.valuation_source || "cost")).replace(/^./, (c) => c.toUpperCase()) },
              ];
              return (
                <div className="pt-3 border-t border-[hsl(var(--border))] space-y-3.5" data-testid="id-valuation-block">
                  {rows.map((r) => (
                    <div key={r.test} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1" data-testid={r.test}>
                      <span className="vp-label whitespace-nowrap">{r.label}</span>
                      <span
                        className={`font-mono font-semibold whitespace-nowrap shrink-0 tabular-nums ${r.tone || ""}`}
                        title={r.title || undefined}
                      >
                        {r.value}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}
            {collection && (
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="vp-label whitespace-nowrap">Collection</span>
                <Link
                  to={`/collections/${collection.id}`}
                  data-testid="id-collection-badge"
                  title="Open collection"
                  className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] truncate max-w-[60%] transition-colors duration-fast"
                >
                  {collection.name}
                </Link>
              </div>
            )}
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="vp-label whitespace-nowrap">Added</span>
              <span className="vp-muted whitespace-nowrap shrink-0">{item.date_added ? new Date(item.date_added).toLocaleDateString() : "—"}</span>
            </div>
          </div>
        </aside>

        <section className="lg:col-span-2 vp-fade-in">
          {(() => {
            // Partition schema/data fields into Item Details vs Wine Details
            // using the SAME name-keyed set ItemEntry.jsx uses for the Create /
            // Edit "Wine Details" card, so the read-only view mirrors the
            // editor without any new schema concept.
            const WINE_DETAILS = new Set([
              "Drinking Window Start",
              "Drinking Window Peak",
              "Drinking Window End",
              "Food Pairing",
              "Aroma",
              "Palate",
              "Finish",
              "Rating",
            ]);
            // Quantity block (2026-02) — every quantity/case field is
            // pulled out of the generic Item Details grid so we can
            // render a dedicated `Quantity` section with the 4-tile
            // cost/valuation summary. All 7 keys ALWAYS render in
            // this fixed order (empty ones display as "—") so the
            // breakdown grid is a stable read-only reflection of the
            // item's stock state — matches the Wine track behaviour.
            const QUANTITY_KEYS = [
              "Total Bottles",
              "Bottle Count",
              "Case Mode",
              "Case Size",
              "Number of Cases",
              "Loose Bottles",
            ];
            const isCollectableTrack = isBottleCollection;
            // Wine Details is a Wine-only card. Non-Wine collections
            // (Whisky, Spirits, and any other type) SKIP this block
            // entirely, even if their item_data happens to hold a
            // legacy `Rating` / `Aroma` / `Palate` key from an earlier
            // schema — that was the Whisky "Wine Details" contamination
            // reported by users.
            const wineKeysAll = isWineCollection
              ? orderedKeys.filter((k) => WINE_DETAILS.has(k))
              : [];
            const regularKeys = orderedKeys.filter(
              (k) => !wineKeysAll.includes(k) && !(isCollectableTrack && QUANTITY_KEYS.includes(k)),
            );
            const isEmptyVal = (v) =>
              v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
            // Only render rows that actually have data — keeps the read-only
            // view clean (empty fields aren't worth real estate here).
            const wineKeysVisible = wineKeysAll.filter((k) => !isEmptyVal(merged[k]));
            // The Wine Details card only appears when the schema declares at
            // least one of these fields AND at least one has a captured value
            // — matches the conditional in ItemEntry.jsx.
            const showWineDetails = wineKeysAll.length > 0 && wineKeysVisible.length > 0;
            return (
              <>
                <div className="vp-card p-6">
                  <h2 className="text-lg font-semibold mb-4">Item Details</h2>
                  {regularKeys.length === 0 ? (
                    <p className="text-sm vp-muted">No details captured.</p>
                  ) : (
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 sm:gap-4" data-testid="id-fields">
                      {regularKeys.map((key) => (
                        <FieldDisplay key={key} name={key} value={merged[key]} schema={fields.find((f) => f.field_name === key)} />
                      ))}
                    </dl>
                  )}
                </div>
                {isCollectableTrack && (() => {
                  // Explicit Quantity section (2026-02 restore).
                  //   • Groups the 7 quantity/case keys previously
                  //     scattered inside "Item Details".
                  //   • Adds a 5-tile Buy Price / Sell / Total Cost /
                  //     Total Value / Remaining Qty summary reading
                  //     from `computeItemValuation` so tiles stay in
                  //     lock-step with the sidebar valuation block.
                  //   • Sell / Valuation surfaces a "Uses Buy"
                  //     fallback footnote when no manual market value
                  //     is set — matches ItemEntry.jsx's
                  //     `sellIsFallback` copy so the two screens
                  //     read the same.
                  //   • Renders identically for Wine, Whisky, Spirits.
                  const money = (n) => `${symbol}${fmt2(Number(n) || 0)}`;
                  const buyUnit = Number(item.cost_price_ex_gst) || 0;
                  // Resolver output: unit value + source label. When
                  // the resolver has no data it returns the "Fallback:
                  // Buy Price (no valuation set)" label so we mirror
                  // that here — the tile footnote reads the same
                  // string the ItemEntry footnote uses.
                  const sellUnit = Number(valuation.current_unit_value) || 0;
                  const sourceLabel = String(valuation.valuation_source || "");
                  const sellIsFallback = sourceLabel === "Awaiting Valuation" || sourceLabel === "No valuation set";
                  const totalCost = Number(valuation.total_cost) || 0;
                  const totalValue = Number(valuation.current_value) || 0;
                  const remainingQty = Number(valuation.remaining_qty) || 0;
                  const tiles = [
                    { test: "id-qty-tile-buy",         label: "Buy Price",        value: money(buyUnit),   foot: "Per bottle" },
                    { test: "id-qty-tile-sell",        label: "Sell / Valuation", value: sellIsFallback ? "Awaiting Valuation" : money(sellUnit),  foot: sellIsFallback ? null : sourceLabel },
                    { test: "id-qty-tile-total-cost",  label: "Total Cost",       value: money(totalCost), foot: `${money(buyUnit)} × ${remainingQty}` },
                    { test: "id-qty-tile-total-value", label: "Total Value",      value: sellIsFallback ? "Awaiting Valuation" : money(totalValue), foot: sellIsFallback ? null : `${money(sellUnit)} × ${remainingQty}` },
                  ];
                  return (
                    <section
                      data-testid="id-quantity-section"
                      className="vp-card p-6 mt-6"
                    >
                      <h2 className="text-lg font-semibold mb-4">Quantity</h2>
                      <dl
                        className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 sm:gap-4"
                        data-testid="id-quantity-fields"
                      >
                        {QUANTITY_KEYS.map((key) => (
                          <FieldDisplay
                            key={key}
                            name={key}
                            value={merged[key]}
                            schema={fields.find((f) => f.field_name === key)}
                          />
                        ))}
                      </dl>
                      <dl
                        className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4"
                        data-testid="id-quantity-tiles"
                      >
                        {tiles.map((t) => (
                          <div
                            key={t.test}
                            data-testid={t.test}
                            className="rounded-md border border-[hsl(var(--border))] px-3 py-2 flex flex-col gap-1 min-w-0 break-words"
                          >
                            <span className="text-[10px] uppercase tracking-wider vp-muted">
                              {t.label}
                            </span>
                            <span className="font-mono text-sm font-semibold tabular-nums text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]">
                              {t.value}
                            </span>
                            {t.foot != null && (
                              <span className="text-[10px] vp-subtle">
                                {t.foot}
                              </span>
                            )}
                          </div>
                        ))}
                      </dl>
                    </section>
                  );
                })()}
                {showWineDetails && (
                  <div className="vp-card p-6 mt-6" data-testid="id-wine-details">
                    <h2 className="text-lg font-semibold mb-4">Wine Details</h2>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 sm:gap-4">
                      {wineKeysVisible.map((key) => (
                        <FieldDisplay
                          key={key}
                          name={key}
                          value={merged[key]}
                          schema={fields.find((f) => f.field_name === key)}
                        />
                      ))}
                    </dl>
                  </div>
                )}
              </>
            );
          })()}
          {item.notes && (
            <div className="vp-card p-6 mt-4" data-testid="id-notes">
              <h2 className="text-lg font-semibold mb-3">Notes</h2>
              <p className="text-sm vp-muted whitespace-pre-wrap leading-relaxed">{item.notes}</p>
            </div>
          )}
        </section>
      </div>

      {/* Sales History — sales recorded against this specific item. */}
      <section className="mt-8" data-testid="id-sales-history">
        <h2 className="text-lg font-semibold mb-3">Sales History</h2>
        <SalesHistoryTable sales={sales} showItemColumn={false} testid="id-sales-history-table" />
      </section>

      <RecordSaleModal
        open={saleModalOpen}
        onOpenChange={setSaleModalOpen}
        item={item}
        onSaleRecorded={() => {
          // Bump the refresh tick — forces the primary loader to
          // re-run so item (cases / loose_units / valuation),
          // sales history, and realised gains all refresh together.
          setRefreshTick((t) => t + 1);
        }}
      />
    </div>
  );
}

function FieldDisplay({ name, value, schema }) {
  const empty = value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
  let rendered;
  if (empty) {
    rendered = <span className="vp-subtle">—</span>;
  } else if (schema?.field_type === "image" && typeof value === "string") {
    rendered = (
      <img
        src={value}
        alt={name}
        loading="lazy"
        className="block h-20 w-20 sm:h-16 sm:w-16 max-w-full object-cover object-center rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]"
      />
    );
  } else if (schema?.field_type === "multi_select" && Array.isArray(value)) {
    rendered = (
      <div className="flex flex-wrap gap-1.5">
        {value.map((v) => <span key={v} className="px-2 py-0.5 text-xs rounded-md bg-[hsl(var(--muted))]">{v}</span>)}
      </div>
    );
  } else if (schema?.field_type === "checkbox") {
    rendered = value ? "Yes" : "No";
  } else if (schema?.field_type === "currency") {
    rendered = <span className="font-mono">${fmt2(value)}</span>;
  } else if (schema?.field_type === "date" && value) {
    rendered = new Date(value).toLocaleDateString();
  } else if (schema?.field_type === "textarea") {
    // Preserve user-entered line breaks for multi-line tasting notes / pairings.
    rendered = <span className="break-words whitespace-pre-wrap leading-relaxed">{String(value)}</span>;
  } else {
    rendered = <span className="break-words">{String(value)}</span>;
  }
  return (
    <div>
      <dt className="vp-label mb-1">{name}</dt>
      <dd className="text-sm">{rendered}</dd>
    </div>
  );
}


/**
 * ItemGallery — read-only display of an item's photo collection.
 *
 *  - Renders one thumbnail per image. No upload / replace / remove affordances
 *    here — all mutations live in Edit mode (ItemEntry.jsx).
 *  - Receives the combined list already redistributed for display by
 *    ItemDetail: index 0 is the primary image, the rest are gallery photos.
 *  - Wrapper carries data-testid="id-gallery" so the section is discoverable
 *    by automation even when the collection is empty.
 */
function ItemGallery({ images }) {
  return (
    <section
      className="vp-card p-4 mb-6 vp-fade-in"
      data-testid="id-gallery"
      aria-label="Item photo gallery"
    >
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold">Photos</h2>
        <span className="text-[10px] uppercase tracking-wider vp-muted" data-testid="id-gallery-count">
          {images.length} image{images.length === 1 ? "" : "s"}
        </span>
      </div>
      {images.length === 0 ? (
        <p className="text-xs vp-muted" data-testid="id-gallery-empty">
          No photos yet. Tap Edit to add images.
        </p>
      ) : (
        // Horizontally scrollable strip — thumbs keep their fixed 80px
        // square (`shrink-0`) and the parent flows on a single row with
        // `overflow-x-auto`, so users can swipe / scroll through any
        // number of photos on mobile, tablet, or desktop without the
        // gallery growing the page vertically.
        <div className="flex gap-3 overflow-x-auto pb-2 px-0.5 -mx-0.5 max-w-full" data-testid="id-gallery-strip">
          {images.map((src, idx) => (
            <GalleryThumb key={idx} src={src} idx={idx} />
          ))}
        </div>
      )}
    </section>
  );
}

function GalleryThumb({ src, idx }) {
  return (
    <div
      className="relative h-24 w-24 sm:h-20 sm:w-20 max-w-full rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] overflow-hidden shrink-0"
      data-testid={`id-gallery-thumb-${idx}`}
    >
      <img
        src={src}
        alt={`Gallery image ${idx + 1}`}
        loading="lazy"
        className="block w-full h-full max-w-full object-cover object-center"
      />
    </div>
  );
}
